import type React from 'react'
import type { CSSProperties } from 'react'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSendMessageMutation, useLazyGetChatHistoryQuery, useGetAvailableModelsQuery } from '@/core/services/medconnect-agent-service'
import { useTranslation } from 'react-i18next'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { keyframes } from '@mui/material/styles'
import colors from '@/assets/scss/_themes-vars.module.scss'

// Import types from the centralized model file
import type { UploadedFileAttachment, DisplayMessage as AgentMsgDisplayType, Author as MessageAuthor } from '@/core/models/medconnect-agent.model'

import { AgentChatInputArea } from './AgentChatInputArea'
import { AgentMessageDisplay } from './AgentMessageDisplay'
import { SuggestedQuestions } from './SuggestedQuestions'
import ChatFeed from '@/components/ui/chat/ChatFeed'
import { Accordion, AccordionDetails, AccordionSummary, Alert, CircularProgress, FormControl, InputLabel, ListSubheader, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import useChatTransport from '../model/useChatTransport'
import useUserPlatformMeta from '../model/useUserPlatformMeta'
import useUserBusinessMeta from '../model/useUserBusinessMeta'
import { normalizeUtf8 } from '@/utils/encoding'

// Import message processing utilities
import { processChatHistoryForDisplay, generateMessageId } from '../model/chatHistoryUtils'

// ─── Streaming tool call indicator ────────────────────────────────────────────

interface StreamingTool {
  id: string
  name: string
  status: 'running' | 'done'
  startedAt: number
  finishedAt?: number
}

const spinKf = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`

function humanizeToolName(name: string) {
  return name.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase())
}

function StreamingToolCard({ tool }: { tool: StreamingTool }) {
  const running = tool.status === 'running'
  const elapsed = tool.finishedAt && tool.startedAt
    ? Math.round((tool.finishedAt - tool.startedAt) / 100) / 10
    : null
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      px: 1.25, py: 0.5, borderRadius: '8px',
      backgroundColor: running ? `${colors.primaryMain}0a` : (colors.gray50 || '#f9fafb'),
      border: `1px solid ${running ? `${colors.primaryMain}30` : (colors.gray200 || '#e5e7eb')}`,
      transition: 'all 0.3s ease',
    }}>
      {running
        ? <BuildCircleOutlinedIcon sx={{ fontSize: 14, color: colors.primaryMain, animation: `${spinKf} 1.5s linear infinite` }} />
        : <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#10b981' }} />
      }
      <Typography sx={{ fontSize: '11px', fontWeight: 500, color: running ? colors.primaryMain : (colors.gray500 || '#6b7280'), lineHeight: 1.3 }}>
        {humanizeToolName(tool.name)}
        {elapsed !== null && (
          <Typography component="span" sx={{ fontSize: '10px', color: colors.gray400 || '#9ca3af', ml: 0.5 }}>
            {elapsed}s
          </Typography>
        )}
      </Typography>
    </Box>
  )
}

// ──────────────────────────────────────────────────────────────────────────────

interface ChatInterfaceProps {
  sessionId: string | null | undefined
  onSessionIdChange?: (sessionId: string) => void
  onMessageSent?: () => void
  agentType?: string
  context?: Record<string, unknown> // Additional context to merge with businessMeta (e.g., expertiseId, patientId)
  onMessageGenerated?: (message: string, messageId: string) => void // Callback when agent generates a message (e.g., for filling form fields)
  initialPrompt?: string // When provided, auto-send this prompt when the session is new (no messages)
  /** Compact rendering for side panel — clean Cloudflare-style messages, no instruction text */
  compact?: boolean
  /** Increment to force a history reload from the DB (e.g. after a broken stream) */
  refreshTick?: number
}

export function ChatInterface(props: ChatInterfaceProps) {
  const { sessionId: propSessionId, onSessionIdChange, onMessageSent, agentType: propAgentType, context: additionalContext, onMessageGenerated, initialPrompt, compact = false, refreshTick } = props
  const { t, i18n } = useTranslation()
  const [messages, setMessages] = useState<AgentMsgDisplayType[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string | null | undefined>(propSessionId)
  const [error, setError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  // Track if we need to update parent with session ID after streaming
  const pendingSessionIdUpdateRef = useRef<string | null>(null)
  const [agentType, setAgentType] = useState<string>(propAgentType || 'patient-consultation')
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [streamingTools, setStreamingTools] = useState<StreamingTool[]>([])
  const { data: availableModels } = useGetAvailableModelsQuery({ discover: true })

  const endOfMessagesRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const streamingMessageRef = useRef<AgentMsgDisplayType | null>(null)
  const initialPromptSentRef = useRef(false)
  // rAF-based delta batching — accumulate tokens between animation frames instead of
  // calling setMessages on every single token (20-50/sec with fast models = browser hang).
  const deltaBufferRef = useRef('')
  const rafIdRef = useRef<number | null>(null)

  const [sendMessage, { isLoading: isSending, error: sendError }] = useSendMessageMutation()
  const [getChatHistory, { data: historyData, isLoading: isLoadingHistory, error: historyError }] = useLazyGetChatHistoryQuery()
  const platformMeta = useUserPlatformMeta()
  const baseBusinessMeta = useUserBusinessMeta()

  // Merge additional context with base businessMeta
  const businessMeta = useMemo(() => {
    if (!additionalContext) return baseBusinessMeta
    return {
      ...baseBusinessMeta,
      ...additionalContext,
    }
  }, [baseBusinessMeta, additionalContext])

  // Track last processed message ID to avoid duplicate callbacks
  const lastProcessedMessageIdRef = useRef<string | null>(null)

  // Listen for generate_expertise_message tool results in history
  useEffect(() => {
    if (onMessageGenerated && historyData?.history && Array.isArray(historyData.history)) {
      // Find the last tool result for generate_expertise_message
      for (let i = historyData.history.length - 1; i >= 0; i--) {
        const msg = historyData.history[i] as any
        if (
          msg.type === 'tool' &&
          msg.tool_call_id &&
          msg.content &&
          (msg._id || msg.id) &&
          (msg._id?.toString() || msg.id) !== lastProcessedMessageIdRef.current
        ) {
          // Check if this is a generate_expertise_message result
          try {
            const toolResult = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content
            if (toolResult && typeof toolResult === 'object' && toolResult.success === true && toolResult.message && typeof toolResult.message === 'string') {
              // Check if the tool call was generate_expertise_message
              const toolCallMsg = historyData.history.find(
                (m: any) =>
                  m.type === 'ai' &&
                  m.tool_calls &&
                  Array.isArray(m.tool_calls) &&
                  m.tool_calls.some(
                    (tc: any) => tc.id === msg.tool_call_id && (tc.name === 'generate_expertise_message' || tc.name === 'generate_and_send_expertise_message')
                  )
              )
              if (toolCallMsg) {
                lastProcessedMessageIdRef.current = msg._id?.toString() || msg.id || null
                onMessageGenerated(toolResult.message, msg._id?.toString() || msg.id)
                break
              }
            }
          } catch (e) {
            // Not a JSON result, continue
          }
        }
      }
    }
  }, [historyData, onMessageGenerated])

  const humanAuthor: MessageAuthor = {
    id: 'human',
    name: t('page.medconnectAgent.chatInterface.humanAuthorName'),
    surname: '',
    renderIcon: (props: { sx?: CSSProperties }) => <Box component="span" sx={props.sx} />,
  }

  const aiAuthor: MessageAuthor = {
    id: 'ai',
    name: t('page.medconnectAgent.chatInterface.aiAuthorName'),
    surname: t('page.medconnectAgent.chatInterface.aiAuthorSurname'),
    renderIcon: (props: { sx?: CSSProperties }) => <Box component="span" sx={props.sx} />,
  }

  useEffect(() => {
    // Cancel any pending rAF delta flush when session changes
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
      deltaBufferRef.current = ''
    }
    setCurrentSessionId(propSessionId)
    if (propSessionId) {
      setMessages([])
      setError(null)
      getChatHistory({ sessionId: propSessionId, agentType: agentType })
    } else {
      setMessages([])
      setError(null)
    }
  }, [propSessionId, agentType, getChatHistory])

  // Reload history from DB when the parent increments refreshTick (e.g. after a broken stream)
  useEffect(() => {
    if (refreshTick && refreshTick > 0 && currentSessionId) {
      getChatHistory({ sessionId: currentSessionId, agentType })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick])

  useEffect(() => {
    if (historyData) {
      if (historyData.history && Array.isArray(historyData.history)) {
        setMessages(processChatHistoryForDisplay(historyData.history, t))
      }
      if (historyData.metadata?.agentType) {
        setAgentType(historyData.metadata.agentType)
      }
      if (historyData.metadata?.model) {
        setSelectedModel(historyData.metadata.model)
      }
    } else if (historyError) {
      console.error('[ChatInterface] Get history error:', historyError)
      const errorMsg = (historyError as any)?.data?.message || t('page.medconnectAgent.chatInterface.failedToLoadHistory')
      setError(`${t('page.medconnectAgent.chatInterface.historyErrorPrefix')}: ${errorMsg}`)
    }
  }, [historyData, historyError, t])

  useEffect(() => {
    if (propAgentType) {
      setAgentType(propAgentType)
    }
  }, [propAgentType])

  useEffect(() => {
    if (sendError) {
      console.error('[ChatInterface] Send message error:', sendError)
      const errorMsg = (sendError as any)?.data?.message || t('page.medconnectAgent.chatInterface.failedToSend')
      setError(`${t('page.medconnectAgent.chatInterface.sendErrorPrefix')}: ${errorMsg}`)
    }
  }, [sendError, t])

  // Auto-scroll is handled by ChatFeed — it only scrolls when the user is near the bottom.

  // Update both internal state and notify parent through callback
  const updateSessionId = useCallback(
    (newSessionId: string) => {
      setCurrentSessionId(newSessionId)

      // If we're currently streaming, store the session ID to notify parent after stream completes
      if (isStreaming) {
        console.log(`[ChatInterface] Delaying session ID update notification until stream completes: ${newSessionId}`)
        pendingSessionIdUpdateRef.current = newSessionId
      } else if (onSessionIdChange) {
        // Otherwise notify parent immediately
        onSessionIdChange(newSessionId)
      }
    },
    [onSessionIdChange, isStreaming]
  )

  const {
    startStream,
    createOrReuseSession,
    uploadFile: uploadFileHook,
    buildToolTagsForCalls,
    isStreaming: isStreamingHook,
  } = useChatTransport({
    currentSessionId,
    updateSessionId,
    setError,
    fetchHistory: (sid) => getChatHistory({ sessionId: sid, agentType }),
    t,
    platformMeta,
    businessMeta,
    agentType,
    language: i18n.language,
    model: selectedModel || undefined,
  })
  useEffect(() => {
    setIsStreaming(isStreamingHook)
  }, [isStreamingHook])

  const handleSendMessage = useCallback(
    async (messageContent: string, filesToUpload: File[]) => {
      const prompt = messageContent.trim()
      if ((!prompt && filesToUpload.length === 0) || isSending || isUploading || isStreaming) return

      setError(null)
      let tempSessionIdForMessage = currentSessionId

      // Handle file uploads first - no changes to this part
      let successfulUploads: UploadedFileAttachment[] = []
      if (filesToUpload.length > 0) {
        // ... existing file upload code ...
        setIsUploading(true)
        try {
          // Create session if it doesn't exist and files are being uploaded
          if (!tempSessionIdForMessage) {
            try {
              const tempSessionResponse = await sendMessage({
                messageContent: '',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                agentType,
                language: i18n.language,
              }).unwrap()
              if (tempSessionResponse.sessionId) {
                tempSessionIdForMessage = tempSessionResponse.sessionId
                updateSessionId(tempSessionIdForMessage)
              } else {
                throw new Error('Failed to obtain session ID for file upload.')
              }
            } catch (err) {
              console.error('[ChatInterface] Failed to create session for file upload:', err)
              setError(t('page.medconnectAgent.chatInterface.failedToInitSessionForUpload'))
              setIsUploading(false)
              return
            }
          }

          const uploadPromises = filesToUpload.map((file) => uploadFileHook(file, tempSessionIdForMessage!))
          const uploadedFileResults = await Promise.all(uploadPromises)
          successfulUploads = uploadedFileResults.filter(Boolean) as UploadedFileAttachment[]

          if (successfulUploads.length === 0 && filesToUpload.length > 0) {
            setError(t('page.medconnectAgent.chatInterface.failedToUploadFiles'))
          }
        } catch (err) {
          console.error('[ChatInterface] File upload error:', err)
          setError(t('page.medconnectAgent.chatInterface.fileUploadFailed', { message: (err as Error).message }))
        } finally {
          setIsUploading(false)
        }
      }

      // Display optimistic user message only if the user typed a prompt
      if (prompt) {
        const userMessageId = generateMessageId()
        const userMessageForDisplay: AgentMsgDisplayType = {
          id: userMessageId,
          type: 'human',
          content: prompt,
          timestamp: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          attachedFiles: successfulUploads.length > 0 ? successfulUploads : undefined,
        }
        setMessages((prev) => [...prev, userMessageForDisplay])
      }

      // Check if we can use SSE
      if (typeof EventSource !== 'undefined') {
        // Start the stream only if there is an actual prompt to send
        if (prompt) {
          // Notify parent component that a message has been sent
          onMessageSent?.()

          // Helper to get or create the streaming message
          const getOrCreateStreamingMessage = (timestamp: string): AgentMsgDisplayType => {
            const existing = streamingMessageRef.current
            if (existing) return existing
            const newMsg: AgentMsgDisplayType = {
              id: `streaming-ai-${currentSessionId || 'new'}`,
              type: 'ai',
              content: '',
              timestamp,
              createdAt: timestamp,
            } as AgentMsgDisplayType
            streamingMessageRef.current = newMsg
            setMessages((prev) => [...prev, newMsg])
            return newMsg
          }

          // Helper to update the streaming message in state
          const updateStreamingMessage = (msg: AgentMsgDisplayType, timestamp: string) => {
            msg.timestamp = timestamp
            msg.createdAt = timestamp as any
            setMessages((prev) => {
              const withoutOld = prev.filter((m) => m.id !== msg.id)
              return [...withoutOld, { ...msg }]
            })
          }

          startStream(
            prompt,
            {
              onHistory: (h) => {
                // Cancel any buffered delta before replacing messages with DB history
                if (rafIdRef.current !== null) {
                  cancelAnimationFrame(rafIdRef.current)
                  rafIdRef.current = null
                  deltaBufferRef.current = ''
                }
                setMessages(processChatHistoryForDisplay(h, t))
                streamingMessageRef.current = null
                setStreamingTools([])
              },
              onDelta: (token) => {
                // Accumulate tokens in a ref and flush once per animation frame.
                // Without this, every token (20-50/sec) calls setMessages with a
                // filter+spread of the full messages array — O(n) per token.
                deltaBufferRef.current += normalizeUtf8(token)
                if (rafIdRef.current === null) {
                  rafIdRef.current = requestAnimationFrame(() => {
                    rafIdRef.current = null
                    const buffered = deltaBufferRef.current
                    if (!buffered) return
                    deltaBufferRef.current = ''
                    const nowIso = new Date().toISOString()
                    const msg = getOrCreateStreamingMessage(nowIso)
                    msg.content = (msg.content || '') + buffered
                    updateStreamingMessage(msg, nowIso)
                  })
                }
              },
              onToolCall: (calls) => {
                const nowIso = new Date().toISOString()
                const msg = getOrCreateStreamingMessage(nowIso)
                msg.content = (msg.content || '') + buildToolTagsForCalls(calls)
                updateStreamingMessage(msg, nowIso)
                // Show live tool status cards
                setStreamingTools(prev => [
                  ...prev,
                  ...calls.map(c => ({ id: c.id, name: c.name, status: 'running' as const, startedAt: Date.now() })),
                ])
              },
              onToolEnd: (_sid) => {
                // Do NOT call getChatHistory or null streamingMessageRef here.
                // With 10 specialists × 5+ tools = 50+ tool-ends per response:
                // - getChatHistory on each = 50 HTTP requests + setMessages floods
                // - nulling streamingMessageRef = new message per tool cycle, messages
                //   array grows unboundedly, each token delta gets slower
                // History is replaced cleanly by onHistory on the completion event.
                setStreamingTools(prev =>
                  prev.map(t => t.status === 'running' ? { ...t, status: 'done' as const, finishedAt: Date.now() } : t)
                )
              },
              onComplete: () => {
                // Flush any remaining buffered delta
                if (rafIdRef.current !== null) {
                  cancelAnimationFrame(rafIdRef.current)
                  rafIdRef.current = null
                  const buffered = deltaBufferRef.current
                  if (buffered) {
                    deltaBufferRef.current = ''
                    const nowIso = new Date().toISOString()
                    const msg = streamingMessageRef.current
                    if (msg) {
                      msg.content = (msg.content || '') + buffered
                      updateStreamingMessage(msg, nowIso)
                    }
                  }
                }
                streamingMessageRef.current = null
                setStreamingTools([])
              },
            },
            tempSessionIdForMessage ?? undefined
          )
        }
      } else {
        // Fall back to regular API for browsers without SSE support
        // Notify parent component that a message has been sent
        onMessageSent?.()
        try {
          const response = await sendMessage({
            sessionId: tempSessionIdForMessage || undefined,
            messageContent: prompt || '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            agentType,
          }).unwrap()

          if (!currentSessionId && response.sessionId) {
            updateSessionId(response.sessionId)
          }

          if (response.chatHistory && Array.isArray(response.chatHistory)) {
            setMessages(processChatHistoryForDisplay(response.chatHistory, t))
          }
        } catch (err) {
          console.error('[ChatInterface] Error in fallback sendMessage:', err)
        }
      }
    },
    [isSending, isUploading, isStreaming, sendMessage, currentSessionId, startStream, updateSessionId, t, onMessageSent, agentType]
  )

  // Auto-send initial prompt when session is new and has no messages
  useEffect(() => {
    if (initialPrompt && !initialPromptSentRef.current && messages.length === 0 && !isLoadingHistory && !isStreaming && !isSending) {
      initialPromptSentRef.current = true
      handleSendMessage(initialPrompt, [])
    }
  }, [initialPrompt, messages.length, isLoadingHistory, isStreaming, isSending, handleSendMessage])

  const isLoadingDisplay = isSending || isUploading || isLoadingHistory || isStreaming

  return (
    <Box
      sx={{
        height: '100%', // Use 100% instead of 100vh to respect parent container
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: compact ? '860px' : '1200px',
        mx: 'auto', // Center the container
        p: compact ? { xs: 0, sm: 0 } : { xs: 1, sm: 2 }, // No outer padding in compact — messages have their own
        minHeight: 0, // Important for flex child to shrink
      }}
    >
      {messages.length === 0 && !error && !isLoadingHistory ? (
        // EMPTY STATE LAYOUT (New Chat View)
        // Content scrolls independently, input stays pinned at bottom
        <>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, sm: 3 },
              overflow: 'auto',
              overscrollBehavior: 'contain',
              minHeight: 0,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Typography variant="h4" sx={{ mb: 4, color: 'text.primary', textAlign: 'center' }}>
              {t('page.medconnectAgent.chatInterface.emptyStateTitle')}
            </Typography>

            {!propAgentType && (
              <Box sx={{ mb: 4, width: '100%', maxWidth: 400 }}>
                <FormControl fullWidth>
                  <InputLabel id="agent-type-select-label">Agent Type</InputLabel>
                  <Select
                    labelId="agent-type-select-label"
                    id="agent-type-select"
                    value={agentType}
                    label="Agent Type"
                    onChange={(e: SelectChangeEvent) => setAgentType(e.target.value)}
                  >
                    <MenuItem value="patient-consultation">Patient Consultation</MenuItem>
                    <MenuItem value="admin">Admin Agent</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {availableModels && availableModels.providers.length > 0 && (() => {
              const activeProviderEntry = availableModels.providers.find((p) => p.id === availableModels.activeProvider)
              const defaultModelName = activeProviderEntry?.catalogModels?.agent
              const currentLabel = selectedModel
                ? selectedModel.split('/').slice(1).join('/') || selectedModel
                : (defaultModelName ?? availableModels.activeProvider)
              return (
                <Box sx={{ mb: 4, width: '100%', maxWidth: 400 }}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="small" />}
                      sx={{ minHeight: 0, p: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Model: {currentLabel}{!selectedModel && ' (default)'}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="model-select-label">Model</InputLabel>
                        <Select
                          labelId="model-select-label"
                          id="model-select"
                          value={selectedModel}
                          label="Model"
                          onChange={(e: SelectChangeEvent) => setSelectedModel(e.target.value)}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                        >
                          <MenuItem value="">
                            <em>{defaultModelName ?? availableModels.activeProvider} (default)</em>
                          </MenuItem>
                          {availableModels.providers.map((p) => {
                            const catalogModelName = p.catalogModels?.agent
                            const discovered = p.discoveredModels ?? []
                            // Catalog model names to avoid duplicates in discovered list
                            const catalogNames = new Set(Object.values(p.catalogModels ?? {}))
                            const extraModels = discovered.filter(m => !catalogNames.has(m.id))

                            return [
                              <ListSubheader key={`header-${p.id}`} sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', lineHeight: '32px' }}>
                                {p.id}
                              </ListSubheader>,
                              // Catalog default (recommended)
                              catalogModelName && (
                                <MenuItem key={`${p.id}/${catalogModelName}`} value={`${p.id}/${catalogModelName}`}>
                                  {catalogModelName} <Typography component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>recommended</Typography>
                                </MenuItem>
                              ),
                              // Other discovered models
                              ...extraModels.map(m => (
                                <MenuItem key={`${p.id}/${m.id}`} value={`${p.id}/${m.id}`} sx={{ fontSize: '0.85rem' }}>
                                  {m.id}
                                </MenuItem>
                              )),
                            ]
                          })}
                        </Select>
                      </FormControl>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )
            })()}

            {/* Suggested Questions */}
            <SuggestedQuestions agentType={agentType} onQuestionClick={async (prompt) => await handleSendMessage(prompt, [])} />
          </Box>

          {/* Input pinned at bottom — never scrolls away */}
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
              width: '100%',
              pt: 1,
              pb: { xs: 1, sm: 2 },
            }}
          >
            <AgentChatInputArea
              onSendMessage={handleSendMessage}
              isLoading={isLoadingDisplay}
              placeholder={t('page.medconnectAgent.chatInterface.inputPlaceholder')}
              hideInstructions={compact}
              compact={compact}
              sx={{
                maxWidth: '768px',
                width: 'calc(100% - 16px)',
                margin: '0 auto',
              }}
            />
          </Box>
        </>
      ) : (
        // MESSAGES PRESENT OR LOADING/ERROR STATE LAYOUT
        <>
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              p: { xs: 1, sm: 2 },
              minHeight: 0,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <ChatFeed>
              {isLoadingHistory && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>{t('page.medconnectAgent.chatInterface.loadingHistory')}</Typography>
                </Box>
              )}
              {messages.map((msg, index) => {
                const isLastMessage = index === messages.length - 1
                const isStreamingMessage = isStreaming && isLastMessage && msg.type === 'ai'
                return <AgentMessageDisplay key={msg.id} message={msg} humanAuthor={humanAuthor} aiAuthor={aiAuthor} isStreaming={isStreamingMessage} compact={compact} />
              })}
              {/* Live tool call status cards — shown during streaming */}
              {isStreaming && streamingTools.length > 0 && (
                <Box sx={{ px: compact ? 1.5 : 2, pb: 0.5 }}>
                  <Stack spacing={0.5} sx={{ ml: compact ? 0 : 4, maxWidth: compact ? '85%' : 320 }}>
                    {streamingTools.map(tool => <StreamingToolCard key={tool.id} tool={tool} />)}
                  </Stack>
                </Box>
              )}
              <div ref={endOfMessagesRef} />
            </ChatFeed>
          </Box>
          <Box
            sx={{
              position: 'relative', // Changed from fixed to relative
              zIndex: 10,
              bgcolor: 'background.default',
              boxShadow: '0px -2px 10px rgba(0,0,0,0.05)',
              width: '100%',
              pt: 1,
              pb: 2,
              flexShrink: 0, // Prevent shrinking
            }}
          >
            {error && (
              <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />} sx={{ mx: { xs: 1, sm: 2 }, mb: 1 }}>
                {error}
              </Alert>
            )}
            <AgentChatInputArea
              onSendMessage={handleSendMessage}
              isLoading={isLoadingDisplay}
              placeholder={t('page.medconnectAgent.chatInterface.inputPlaceholder')}
              hideInstructions={compact}
              compact={compact}
            />
          </Box>
          {/* Streaming indicator — hidden in compact mode (send button spinner is enough) */}
          {isStreaming && !compact && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '120px',
                right: '20px',
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'secondary.main',
                color: 'white',
                borderRadius: '20px',
                px: 2,
                py: 0.5,
              }}
            >
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              <Typography variant="caption">{t('page.medconnectAgent.chatInterface.streamingIndicator')}</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default ChatInterface
